import { useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useNavigate, useLocation } from '@remix-run/react';
import { toast } from 'sonner';
import axios from 'axios';

declare global {
  interface Window {
    ENV: {
      API_URL: string;
    };
  }
}

let socketInstance: Socket | null = null;
let activeConnections = 0;

const recentlyDisplayedNotifications = new Set<string>();

const trackDisplayedNotification = (notificationId: string) => {
  recentlyDisplayedNotifications.add(notificationId);
  setTimeout(() => {
    recentlyDisplayedNotifications.delete(notificationId);
  }, 10000);
};

const isRecentlyDisplayed = (notificationId: string): boolean => {
  return recentlyDisplayedNotifications.has(notificationId);
};


const isSameBaseUrl = (url1: string, url2: string): boolean => {
  const base1 = url1.split('?')[0];
  const base2 = url2.split('?')[0];
  return base1 === base2;
};

const markNotificationAsRead = async (notificationId: string): Promise<void> => {
  try {
    const formData = new FormData();
    formData.append('isRead', 'true');
    formData.append('isReceiverRead', 'true');
    const response = await axios.post(
      `/api/read-notify/${notificationId}`,
      formData,
      {
        headers: {    
          "Content-Type": "application/x-www-form-urlencoded",
        },
        withCredentials: true
      }
    );
    
    if (response?.status !== 200) {
      const errorText = response?.data;
      throw new Error(`Could not update notification status: ${response?.statusText}, Details: ${errorText}`);
    }
    
    const data = response?.data;
    console.log(`[DEBUG] Notification ${notificationId} has been marked as read, Response:`, data);
    return data;
  } catch (error) {
    console.error('[DEBUG] Error marking notification as read:', error);
    throw error;
  }
};

const addRefreshToUrl = (url: string): string => {
  const timestamp = new Date().getTime();
  
  if (url.includes('?')) {
    return `${url}&_refresh=${timestamp}`;
  } else {
    return `${url}?_refresh=${timestamp}`;
  }
};

const isClient = typeof window !== 'undefined';

export const useNotificationsSocket = (userId: string | undefined) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [newNotification, setNewNotification] = useState<any>(null);
  const [lastPong, setLastPong] = useState<string | null>(null);
  
  // Chỉ sử dụng các hooks navigation khi ở client-side
  const navigate = isClient ? useNavigate() : null;
  const location = isClient ? useLocation() : { pathname: '' };

  const currentUserIdRef = useRef<string | undefined>(userId);
  const currentLocationRef = useRef(location?.pathname);
  
  useEffect(() => {
    if (isClient) {
      currentUserIdRef.current = userId;
    }
  }, [userId]);
  
  useEffect(() => {
    if (isClient) {
      currentLocationRef.current = location?.pathname;
    }
  }, [location]);

  const apiUrl = import.meta.env.VITE_WEBSOCKET_URL ||  'http://localhost:8080';
  const sendPing = useCallback(() => {
    if (socket && isConnected) {
      console.log('[DEBUG] Sending ping to server');
      socket.emit('ping');
    }
  }, [socket, isConnected]);
  
  const handleNotificationClick = useCallback((notification: any) => {
    if (!isClient || !navigate) return;

    markNotificationAsRead(notification.id)
      .then(() => {
        if (isSameBaseUrl(notification.link, window.location.pathname)) {
          console.log('[DEBUG] Currently on the same page as the notification, reloading page...');
          window.location.reload();
        } else {
          const refreshedUrl = addRefreshToUrl(notification.link);
          navigate(refreshedUrl);
        }
      })
      .catch((err) => {
        if (isSameBaseUrl(notification.link, window.location.pathname)) {
          window.location.reload();
        } else {
          const refreshedUrl = addRefreshToUrl(notification.link);
          navigate(refreshedUrl);
        }
      });
  }, [navigate]);
  
  const handleNotificationDisplay = useCallback((notification: any) => {
    if (!isClient) return;
    
    if (!notification || !notification.id) {
      console.warn('[DEBUG] Received notification without ID, ignoring');
      return;
    }

    if (notification.targetUserId && notification.targetUserId !== userId) {
      console.log(`[DEBUG] Notification ${notification.id} is not for current user, skipping`);
      return;
    }
    
    if (notification.creatorId && notification.creatorId === userId) {
      console.log(`[DEBUG] Notification ${notification.id} was created by current user, skipping`);
      return;
    }
    
    if (isRecentlyDisplayed(notification.id)) {
      console.log(`[DEBUG] Notification ${notification.id} already displayed, skipping`);
      return;
    }
    
    console.log('[DEBUG] Displaying new notification:', notification);
    setNewNotification(notification);
    
    trackDisplayedNotification(notification.id);
    
    setTimeout(() => {
      try {
        toast.info(notification.title, {
          description: notification.description,
          duration: 10000,
          action: {
            label: 'View details',
            onClick: () => handleNotificationClick(notification)
          },
        });
        
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification(notification.title, {
            body: notification.description,
            icon: '/logo.png'
          });
        }
        
        console.log('[DEBUG] Notification toast displayed successfully');
      } catch (toastError) {
        console.error('[DEBUG] Error displaying toast:', toastError);
      }
    }, 100);
  }, [userId, handleNotificationClick]);
  
  useEffect(() => {
    if (!isClient || !userId) {
      console.log('[DEBUG] Not in client or no userId provided, not connecting to WebSocket');
      return;
    }

    activeConnections++;
    console.log(`[DEBUG] Active connections: ${activeConnections}`);

    if (socketInstance && socketInstance.connected) {
      console.log('[DEBUG] Reusing existing socket connection');
      setSocket(socketInstance);
      setIsConnected(true);
      
      const onNewNotification = (notification: any) => {
        if (notification.targetUserId && notification.targetUserId !== userId) {
          console.log(`[DEBUG] Notification is not for current user (${userId}), skipping`);
          return;
        }
        if (notification.creatorId && notification.creatorId === userId) {
          console.log(`[DEBUG] Notification was created by current user (${userId}), skipping`);
          return;
        }
        console.log('[DEBUG] New notification received on reused socket:', notification);
        handleNotificationDisplay(notification);
      };
      
      socketInstance.off('newNotification');
      socketInstance.on('newNotification', onNewNotification);
      
      return () => {
        activeConnections--;
        console.log(`[DEBUG] Component unmounting. Active connections: ${activeConnections}`);
        
        if (activeConnections === 0 && socketInstance) {
          console.log('[DEBUG] No more active connections, disconnecting socket');
          socketInstance.disconnect();
          socketInstance = null;
        }
      };
    }

    console.log(`[DEBUG] Attempting to connect to WebSocket at: ${apiUrl} with userId: ${userId}`);

    const socketIo = io(apiUrl, {
      path: '/websocket/',
      query: { userId },
      withCredentials: true,
      transports: ['websocket','polling'],
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      timeout: 20000,
      autoConnect: true
    });

    socketIo.on('connect_error', (err) => {
      console.error('[DEBUG] WebSocket connection error:', err.message);
      setIsConnected(false);
    });

    socketIo.on('connect', () => {
      console.log(`[DEBUG] WebSocket connected successfully with socketId: ${socketIo.id}`);
      setIsConnected(true);
      
      console.log(`[DEBUG] Subscribing to notifications for userId: ${userId}`);
      socketIo.emit('subscribeToNotifications', userId);

      setTimeout(() => socketIo.emit('ping'), 1000);
    });

    socketIo.on('disconnect', (reason) => {
      console.log(`[DEBUG] WebSocket disconnected. Reason: ${reason}`);
      setIsConnected(false);
      
      if (reason === 'io server disconnect') {
        console.log('[DEBUG] Attempting to reconnect...');
        socketIo.connect();
      }
    });

    socketIo.on('subscriptionConfirmed', (data) => {
      console.log('[DEBUG] Subscription confirmed:', data);
    });

    socketIo.on('pong', (data) => {
      console.log('[DEBUG] Received pong:', data);
      setLastPong(data.timestamp);
    });

    socketIo.on('error', (err) => {
      console.error('[DEBUG] WebSocket error:', err);
    });

    socketIo.on('newNotification', (notification) => {
      if (notification.targetUserId && notification.targetUserId !== userId) {
        console.log(`[DEBUG] Notification is not for current user (${userId}), skipping`);
        return;
      }
      if (notification.creatorId && notification.creatorId === userId) {
        console.log(`[DEBUG] Notification was created by current user (${userId}), skipping`);
        return;
      }
      console.log('[DEBUG] New notification received:', notification);
      handleNotificationDisplay(notification);
    });

    socketIo.on('globalNotification', (notification) => {
      console.log('[DEBUG] Global notification received:', notification);
      
      if (notification.targetUserId === userId && notification.creatorId !== userId) {
        console.log('[DEBUG] This global notification is for current user and not created by them, processing...');
        handleNotificationDisplay(notification);
      } else {
        console.log('[DEBUG] Global notification not for this user or created by them, ignoring');
      }
    });

    socketInstance = socketIo;
    setSocket(socketIo);

    const pingInterval = setInterval(() => {
      if (socketIo.connected) {
        socketIo.emit('ping');
      } else {
        console.log('[DEBUG] Socket not connected, attempting reconnect...');
        socketIo.connect();
      }
    }, 30000);
    
    return () => {
      activeConnections--;
      console.log(`[DEBUG] Component unmounting. Active connections: ${activeConnections}`);
      
      clearInterval(pingInterval);
      
      if (activeConnections === 0) {
        console.log('[DEBUG] No more active connections, disconnecting socket');
        socketIo.disconnect();
        socketInstance = null;
      }
    };
  }, [userId, navigate, handleNotificationDisplay]);

  return { 
    socket, 
    isConnected, 
    newNotification, 
    lastPong,
    sendPing 
  };
}; 